import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Chip, Grid, Paper, Snackbar, Skeleton, Table, TableHead, TableRow, TableBody, TableCell, Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ReportFilter, { updateReportParams } from './components/ReportFilter';
import { useTranslation } from '../common/components/LocalizationProvider';
import PageLayout from '../common/components/PageLayout';
import ReportsMenu from './components/ReportsMenu';
import { useCatch } from '../reactHelper';
import useReportStyles from './common/useReportStyles';
import TableShimmer from '../common/components/TableShimmer';
import scheduleReport from './common/scheduleReport';
import fetchOrThrow from '../common/util/fetchOrThrow';
import SelectField from '../common/components/SelectField';
import { buildSummaryClientViewModel } from './summaryClientViewModel';
import { buildClientRowsFromRoutes } from './summaryClientDomain';
import exportExcel from '../common/util/exportExcel';
import exportTablePdf from '../common/util/exportTablePdf';
import ExportFallbackActions from './components/ExportFallbackActions';
import { useAdministrator } from '../common/util/permissions';

const baseColumns = ['Dispositivo', 'Placa', 'Primeiro movimento', 'Último movimento', 'Distância', 'Velocidade Média', 'Velocidade Máxima', 'Km inicial', 'Km final', 'Viagens', 'Motor Ligado', 'Em movimento', 'Parado (motor ligado)', 'Média de ociosidade por viagem', '% ocioso', 'Status'];

const SummaryReportPage = () => {
  const navigate = useNavigate();
  const { classes } = useReportStyles();
  const t = useTranslation();
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const daily = searchParams.get('daily') === 'true';
  const devices = useSelector((state) => state.devices.items);
  const groups = useSelector((state) => state.groups.items);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportUrl, setExportUrl] = useState('');
  const [exportMessage, setExportMessage] = useState({ open: false, severity: 'info', text: '' });
  const [queryMessage, setQueryMessage] = useState({ open: false, severity: 'info', text: '' });
  const isAdmin = useAdministrator();
  const columns = useMemo(() => (isAdmin ? [...baseColumns, 'Status técnico'] : baseColumns), [isAdmin]);

  const resolveDeviceIds = ({ selectedDeviceIds = [], selectedGroupIds = [] }) => {
    const result = new Set((selectedDeviceIds || []).filter(Boolean).map(Number));
    const groupSet = new Set((selectedGroupIds || []).filter(Boolean).map(Number));
    if (!groupSet.size) return [...result];

    const stack = [...groupSet];
    while (stack.length) {
      const current = stack.pop();
      Object.values(groups).forEach((group) => {
        if (group?.groupId === current && !groupSet.has(group.id)) {
          groupSet.add(group.id);
          stack.push(group.id);
        }
      });
    }

    Object.values(devices).forEach((device) => {
      if (groupSet.has(device.groupId)) result.add(device.id);
    });
    return [...result];
  };

  const onShow = useCatch(async ({ deviceIds, groupIds, from, to }) => {
    setQueryMessage({ open: false, severity: 'info', text: '' });
    setLoading(true);
    const targetDeviceIds = resolveDeviceIds({ selectedDeviceIds: deviceIds, selectedGroupIds: groupIds });
    if (!targetDeviceIds.length) {
      setItems([]);
      setQueryMessage({
        open: true,
        severity: 'warning',
        text: 'Selecione ao menos um dispositivo ou grupo para consultar.',
      });
      setLoading(false);
      return;
    }

    try {
      const rows = await buildClientRowsFromRoutes({
        deviceIds: targetDeviceIds,
        from,
        to,
        devices,
        fetchJson: async (url) => {
          const response = await fetchOrThrow(url, { headers: { Accept: 'application/json' } });
          return response.json();
        },
        concurrency: 5,
      });
      setItems(rows);
    } catch (error) {
      const canceled = error?.name === 'AbortError' || error?.message?.toLowerCase?.().includes('abort');
      setItems([]);
      setQueryMessage({
        open: true,
        severity: canceled ? 'warning' : 'error',
        text: canceled
          ? 'Consulta cancelada antes de concluir.'
          : 'Não foi possível concluir a consulta do relatório.',
      });
      throw error;
    } finally {
      setLoading(false);
    }
  });

  const onSchedule = useCatch(async (deviceIds, groupIds, report) => {
    report.type = 'summary';
    report.attributes.daily = daily;
    await scheduleReport(deviceIds, groupIds, report);
    navigate('/reports/scheduled');
  });

  const vm = useMemo(() => buildSummaryClientViewModel([], null, items), [items]);

  const closeExportMessage = () => {
    setExportMessage((prev) => ({ ...prev, open: false }));
  };

  const clearExportFallback = useCallback(() => {
    setExportUrl((prev) => {
      if (prev?.startsWith('blob:')) {
        URL.revokeObjectURL(prev);
      }
      return '';
    });
  }, []);

  useEffect(() => () => {
    if (exportUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(exportUrl);
    }
  }, [exportUrl]);

  const onExport = useCatch(async ({
    deviceIds = [],
    groupIds = [],
    format = 'xlsx',
  } = {}) => {
    if (loading) {
      setExportMessage({
        open: true,
        severity: 'warning',
        text: 'Aguarde o término da consulta antes de exportar.',
      });
      return;
    }

    const hasDeviceSelection = deviceIds.length || groupIds.length;
    if (!hasDeviceSelection || !vm.rows.length) {
      setExportMessage({
        open: true,
        severity: 'warning',
        text: 'Nenhum dado para exportar no período selecionado.',
      });
      return;
    }

    const normalizedFormat = String(format).toLowerCase();
    const sheetRows = vm.rows.map((row) => {
      const exportRow = {};
      columns.forEach((column) => {
        exportRow[column] = row?.[column] ?? '';
      });
      return exportRow;
    });

    const fallbackCsvUrl = (() => {
      if (!sheetRows.length) {
        return '';
      }
      const header = columns.join(';');
      const lines = sheetRows.map((row) => columns.map((column) => {
        const cellValue = row?.[column] ?? '';
        return `"${String(cellValue).replaceAll('"', '""')}"`;
      }).join(';'));
      const csvContent = [header, ...lines].join('\n');
      return URL.createObjectURL(new Blob([csvContent], { type: 'text/csv;charset=utf-8;' }));
    })();

    clearExportFallback();

    try {
      if (normalizedFormat === 'pdf') {
        await exportTablePdf(t('reportSummary'), 'summary.pdf', columns, sheetRows);
      } else {
        await exportExcel(t('reportSummary'), 'summary.xlsx', new Map([[t('reportSummary'), sheetRows]]), theme);
      }
      setExportMessage({
        open: true,
        severity: 'success',
        text: 'Exportação concluída com sucesso.',
      });
    } catch {
      setExportUrl(fallbackCsvUrl);
      setExportMessage({
        open: true,
        severity: 'error',
        text: fallbackCsvUrl
          ? 'Não foi possível exportar o relatório agora. Use o link alternativo abaixo para baixar/copiar o arquivo.'
          : 'Não foi possível exportar o relatório agora. Tente novamente em instantes.',
      });
    }
  });

  return (
    <PageLayout menu={<ReportsMenu />} breadcrumbs={['reportTitle', 'reportSummary']}>
      <div className={classes.header}>
        <Box sx={loading ? { pointerEvents: 'none', opacity: 0.6 } : undefined}>
          <ReportFilter onShow={onShow} onExport={onExport} onSchedule={onSchedule} deviceType="multiple" loading={loading}>
            <div className={classes.filterItem}>
              <SelectField
                label={t('sharedType')}
                value={String(daily)}
                onChange={(e) => updateReportParams(searchParams, setSearchParams, 'daily', [e.target.value])}
                fullWidth
                searchPlaceholder={t('sharedSearch')}
                data={[{ key: 'false', title: t('reportSummary') }, { key: 'true', title: t('reportDaily') }]}
                keyGetter={(item) => item.key}
                titleGetter={(item) => item.title}
                disabled={loading}
              />
            </div>
          </ReportFilter>
          <ExportFallbackActions exportUrl={exportUrl} onClear={clearExportFallback} />
        </Box>
        {loading && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Consultando dados do período...
          </Typography>
        )}
        {queryMessage.open && (
          <Alert severity={queryMessage.severity} sx={{ mt: 1 }}>
            {queryMessage.text}
          </Alert>
        )}
      </div>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {loading
          ? [...Array(6)].map((_, index) => (
            <Grid key={`card-skeleton-${String(index)}`} size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
              <Paper sx={{ p: 2 }}>
                <Skeleton variant="text" width="75%" />
                <Skeleton variant="text" width="55%" height={34} />
              </Paper>
            </Grid>
          ))
          : vm.cards.map((card) => (
            <Grid key={card.label} size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">{card.label}</Typography>
                <Typography variant="h6">{card.value}</Typography>
              </Paper>
            </Grid>
          ))}
      </Grid>

      <div className={classes.tableCard}>
        <div className={classes.tableScroller}>
          <Table className={classes.table}>
            <TableHead><TableRow>{columns.map((col) => <TableCell key={col}>{col}</TableCell>)}</TableRow></TableHead>
            <TableBody>
              {!loading ? vm.rows.map((row) => {
                const isAmber = row.nivel_cliente === 'SEM_DESLOCAMENTO' || row.nivel_cliente === 'PROVAVEL_REBOQUE';
                return (
                  <TableRow key={`${row.Dispositivo}_${row['Primeiro movimento']}`} sx={isAmber ? { backgroundColor: '#FFF8E8' } : undefined}>
                    {columns.map((col) => {
                      if (col === 'Status') {
                        return <TableCell key={col}><Chip size="small" color={isAmber ? 'warning' : 'success'} label={row.Status ?? row.status_cliente} /></TableCell>;
                      }
                      if (col === 'Status técnico') {
                        return <TableCell key={col}>{row.status_tecnico || '—'}</TableCell>;
                      }
                      return <TableCell key={col}>{row[col]}</TableCell>;
                    })}
                  </TableRow>
                );
              }) : <TableShimmer columns={columns.length} />}
            </TableBody>
          </Table>
        </div>
      </div>
      <Box mt={1}>
        <Typography variant="caption" color="text.secondary">
          {isAdmin
            ? 'Visão admin: status do cliente com coluna técnica de suporte habilitada.'
            : 'Visão cliente: sem colunas técnicas de suporte.'}
        </Typography>
      </Box>
      <Snackbar open={exportMessage.open} autoHideDuration={5000} onClose={closeExportMessage}>
        <Alert severity={exportMessage.severity} onClose={closeExportMessage} variant="filled">
          {exportMessage.text}
        </Alert>
      </Snackbar>
    </PageLayout>
  );
};

export default SummaryReportPage;
